import { useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid2';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { ApiError } from '../api/client';
import { clientsApi, invoicesApi, subscriptionsApi } from '../api/endpoints';
import { PageHeader } from '../components/PageHeader';
import { StatusBadge } from '../components/StatusBadge';
import { EmptyState } from '../components/EmptyState';
import { ClientFormDialog } from '../features/clients/ClientFormDialog';
import { SubscriptionFormDialog } from '../features/clients/SubscriptionFormDialog';
import { RecordPaymentDialog } from '../features/payments/RecordPaymentDialog';
import { useToast } from '../app/ToastProvider';
import { useAuth } from '../app/AuthProvider';
import { formatDate, formatDateTime, formatPeso } from '../utils/format';

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {value ?? '—'}
      </Typography>
    </Box>
  );
}

export function ClientDetailPage() {
  const { user } = useAuth();
  const canManageClient = user?.role === 'ORG_ADMIN' || user?.role === 'MANAGER' || user?.role === 'BILLING';
  const { id } = useParams<{ id: string }>();
  const clientId = Number(id);

  const [editOpen, setEditOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [reactivationPayment, setReactivationPayment] = useState(false);
  const [subscriptionEditOpen, setSubscriptionEditOpen] = useState(false);

  const toast = useToast();
  const queryClient = useQueryClient();

  const { data: client, isPending, isError, error } = useQuery({
    queryKey: ['client', clientId],
    queryFn: () => clientsApi.detail(clientId),
    enabled: Number.isFinite(clientId),
  });

  const subscription = client?.subscriptions[0];

  const generateInvoice = useMutation({
    mutationFn: () => invoicesApi.create({ subscriptionId: subscription?.id as number }),
    onSuccess: async ({ data, message }) => {
      toast.success(message);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['client', clientId] }),
        queryClient.invalidateQueries({ queryKey: ['invoices'] }),
        queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
      ]);
      try {
        await invoicesApi.download(data.id, data.invoiceNumber);
      } catch {
        toast.notify(
          'The invoice was generated, but its PDF could not be downloaded. You can retry from the invoice table.',
          'warning',
        );
      }
    },
    onError: (caught: unknown) => {
      toast.error(
        caught instanceof ApiError ? caught.message : 'The invoice could not be generated.',
      );
    },
  });

  const changeStatus = useMutation({
    mutationFn: (status: 'ACTIVE' | 'SUSPENDED' | 'PULLED_OUT') =>
      subscriptionsApi.update(subscription?.id as number, { status }),
    onSuccess: async ({ message }) => {
      toast.success(message);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['client', clientId] }),
        queryClient.invalidateQueries({ queryKey: ['clients'] }),
        queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
      ]);
    },
    onError: (caught: unknown) =>
      toast.error(caught instanceof ApiError ? caught.message : 'The subscription status could not be changed.'),
  });
  if (isPending) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 320 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !client) {
    return (
      <Alert severity="error">
        {error instanceof Error ? error.message : 'This client could not be loaded.'}
      </Alert>
    );
  }

  const address = client.addresses.find((item) => item.isPrimary) ?? client.addresses[0];
  const fullAddress = address
    ? [
        address.addressLine1,
        address.addressLine2,
        address.barangay,
        address.municipality,
        address.province,
        address.postalCode,
      ]
        .filter(Boolean)
        .join(', ')
    : '—';

  const hasPayableInvoice = client.invoices.some(
    (invoice) =>
      (invoice.status === 'ISSUED' || invoice.status === 'PARTIALLY_PAID') &&
      invoice.outstandingAmount > 0,
  );
  const hasUnsettledInvoice = client.invoices.some(
    (invoice) => invoice.status !== 'PAID' && invoice.status !== 'VOID',
  );
  const canGenerateInvoice =
    Boolean(subscription) &&
    (subscription?.status === 'ACTIVE' || subscription?.status === 'SUSPENDED') &&
    !hasUnsettledInvoice;

  return (
    <>
      <Breadcrumbs sx={{ mb: 1 }}>
        <Link component={RouterLink} to="/clients" variant="body2">
          Clients
        </Link>
        <Typography variant="body2" color="text.primary">
          {client.clientNumber}
        </Typography>
      </Breadcrumbs>

      <PageHeader
        title={client.displayName}
        description={`${client.clientNumber} · ${client.customerType === 'BUSINESS' ? 'Business account' : 'Residential account'}`}
        actions={
          <>
            {canManageClient && subscription?.status === 'PENDING' ? (
              <Button variant="contained" color="success" disabled={changeStatus.isPending} onClick={() => changeStatus.mutate('ACTIVE')}>
                Approve & Activate
              </Button>
            ) : null}
            {canManageClient && subscription?.status === 'ACTIVE' ? (
              <Button variant="outlined" color="warning" disabled={changeStatus.isPending} onClick={() => changeStatus.mutate('SUSPENDED')}>
                Suspend
              </Button>
            ) : null}
            {canManageClient && subscription?.status === 'SUSPENDED' ? (
              <Button
                variant="contained"
                color="success"
                onClick={() => {
                  setReactivationPayment(true);
                  setPaymentOpen(true);
                }}
              >
                Reactivate Subscription
              </Button>
            ) : null}
            {canManageClient && subscription && subscription.status !== 'PULLED_OUT' ? (
              <Button variant="outlined" color="error" disabled={changeStatus.isPending} onClick={() => changeStatus.mutate('PULLED_OUT')}>
                Mark Pulled Out
              </Button>
            ) : null}
            {canManageClient ? <Button
              variant="outlined"
              startIcon={<ReceiptLongOutlinedIcon />}
              disabled={!canGenerateInvoice || generateInvoice.isPending}
              onClick={() => generateInvoice.mutate()}
            >
              {generateInvoice.isPending ? 'Generating…' : 'Generate Invoice'}
            </Button> : null}
            {canManageClient ? <Button
              variant="outlined"
              startIcon={<EditOutlinedIcon />}
              onClick={() => setEditOpen(true)}
            >
              Edit Client
            </Button> : null}
            {canManageClient ? <Button
              variant="contained"
              startIcon={<PaymentsOutlinedIcon />}
              disabled={!hasPayableInvoice}
              onClick={() => {
                setReactivationPayment(false);
                setPaymentOpen(true);
              }}
            >
              Record Payment
            </Button> : null}
          </>
        }
      />

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <Card sx={{ height: '100%' }}>
            <CardHeader title="Customer Information" titleTypographyProps={{ variant: 'h6' }} />
            <Divider />
            <CardContent>
              <Stack spacing={2}>
                <Field label="Client Number" value={client.clientNumber} />
                <Field label="Customer Type" value={<StatusBadge status={client.customerType} />} />
                {client.customerType === 'BUSINESS' ? (
                  <Field label="Contact Person" value={client.contactPerson} />
                ) : null}
                <Field label="Mobile Number" value={client.mobileNumber} />
                <Field label="Email Address" value={client.email} />
                <Field label="Service Address" value={fullAddress} />
                <Field
                  label="Account Status"
                  value={<StatusBadge status={client.isActive ? 'ACTIVE' : 'INACTIVE'} />}
                />
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <Card sx={{ height: '100%' }}>
            <CardHeader title="Subscription Information" titleTypographyProps={{ variant: 'h6' }} action={canManageClient && subscription ? <Button size="small" startIcon={<EditOutlinedIcon />} onClick={() => setSubscriptionEditOpen(true)}>Edit</Button> : null} />
            <Divider />
            <CardContent>
              {subscription ? (
                <Stack spacing={2}>
                  <Field label="Subscription Number" value={subscription.subscriptionNumber} />
                  <Field label="Status" value={<StatusBadge status={subscription.status} />} />
                  <Field
                    label="Service Plan"
                    value={`${subscription.servicePlan.planName} — ${formatPeso(subscription.servicePlan.monthlyFee)}/month`}
                  />
                  <Field
                    label="Speed"
                    value={`${subscription.servicePlan.downloadSpeedMbps} Mbps down / ${subscription.servicePlan.uploadSpeedMbps} Mbps up`}
                  />
                  <Field label="Branch" value={subscription.branch} />
                  <Field label="Billing Day" value={`Day ${subscription.billingDay} of each month`} />
                  <Field label="Next Due Date" value={formatDate(subscription.nextDueDate)} />
                  <Field label="Activation Date" value={formatDate(subscription.activationDate)} />
                  <Field label="PPPoE Username" value={subscription.pppoeUsername} />
                  <Field label="PPPoE Password" value={subscription.pppoePassword} />
                  <Field label="Installation Fee" value={formatPeso(subscription.installationFee)} />
                  <Field label="Agent / Referral" value={subscription.agentReferral} />
                  <Field label="Installer(s)" value={subscription.installerNames} />
                  <Field label="Network Port" value={[subscription.smt && 'SMT ' + subscription.smt.replace(/^SMT\s*/i, ''), subscription.lcp && 'LCP ' + subscription.lcp.replace(/^LCP\s*/i, ''), subscription.nap && 'NAP ' + subscription.nap.replace(/^NAP\s*/i, ''), subscription.portNumber && 'Port ' + subscription.portNumber.replace(/^Port\s*/i, '')].filter(Boolean).join(' / ')} />
                  <Field label="Auto Suspension" value={subscription.autoSuspendEnabled ? `After ${subscription.gracePeriodDays} grace day(s)` : 'Disabled'} />
                </Stack>
              ) : (
                <EmptyState
                  message="No subscription yet."
                  hint="This client has no internet service linked to their account."
                />
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, lg: 4 }}>
          <Card sx={{ height: '100%' }}>
            <CardHeader title="Billing Summary" titleTypographyProps={{ variant: 'h6' }} />
            <Divider />
            <CardContent>
              <Stack spacing={2.5}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Outstanding Balance
                  </Typography>
                  <Typography
                    variant="h4"
                    color={client.billingSummary.outstandingBalance > 0 ? 'error.main' : 'success.main'}
                  >
                    {formatPeso(client.billingSummary.outstandingBalance)}
                  </Typography>
                  {client.billingSummary.isOverdue ? (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      This account has an invoice past its due date.
                    </Alert>
                  ) : null}
                </Box>
                <Divider />
                <Field label="Total Paid" value={formatPeso(client.billingSummary.totalPaid)} />
                <Field label="Invoices Issued" value={client.billingSummary.invoiceCount} />
                <Field label="Next Due Date" value={formatDate(client.billingSummary.nextDueDate)} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, lg: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardHeader title="Invoices" titleTypographyProps={{ variant: 'h6' }} />
            <Divider />
            <CardContent sx={{ p: 0 }}>
              {client.invoices.length === 0 ? (
                <EmptyState message="No invoices issued yet." />
              ) : (
                <TableContainer sx={{ maxHeight: 420 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Invoice</TableCell>
                        <TableCell>Due Date</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell align="right">Outstanding</TableCell>
                        <TableCell align="right">Download</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {client.invoices.map((invoice) => (
                        <TableRow key={invoice.id} hover>
                          <TableCell>{invoice.invoiceNumber}</TableCell>
                          <TableCell>{formatDate(invoice.dueDate)}</TableCell>
                          <TableCell>
                            <StatusBadge status={invoice.status} />
                          </TableCell>
                          <TableCell align="right">{formatPeso(invoice.totalAmount)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            {formatPeso(invoice.outstandingAmount)}
                          </TableCell>                          <TableCell align="right">
                            <Button size="small" onClick={() => invoicesApi.download(invoice.id, invoice.invoiceNumber)}>PDF</Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, lg: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardHeader title="Payment History" titleTypographyProps={{ variant: 'h6' }} />
            <Divider />
            <CardContent sx={{ p: 0 }}>
              {client.payments.length === 0 ? (
                <EmptyState message="No payments recorded for this client yet." />
              ) : (
                <TableContainer sx={{ maxHeight: 420 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Payment</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Method</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {client.payments.map((payment) => (
                        <TableRow key={payment.id} hover>
                          <TableCell>{payment.paymentNumber}</TableCell>
                          <TableCell>{formatDate(payment.paymentDate)}</TableCell>
                          <TableCell>
                            <StatusBadge status={payment.paymentMethod} />
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontWeight: 600,
                              textDecoration: payment.isVoid ? 'line-through' : 'none',
                              color: payment.isVoid ? 'text.disabled' : 'inherit',
                            }}
                          >
                            {formatPeso(payment.amountReceived)}
                          </TableCell>
                          <TableCell>
                            <StatusBadge status={payment.isVoid ? 'VOID' : 'PAID'} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={12}>
          <Card>
            <CardHeader title="Notes" titleTypographyProps={{ variant: 'h6' }} />
            <Divider />
            <CardContent>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {client.notes || 'No notes recorded for this client.'}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
                Created {formatDateTime(client.createdAt)} by {client.createdBy.username} · Last
                updated {formatDateTime(client.updatedAt)} by {client.updatedBy.username}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <ClientFormDialog open={editOpen} client={client} onClose={() => setEditOpen(false)} />
      {subscription ? <SubscriptionFormDialog open={subscriptionEditOpen} subscription={subscription} onClose={() => setSubscriptionEditOpen(false)} /> : null}
      <RecordPaymentDialog
        open={paymentOpen}
        presetClient={{ id: client.id, label: `${client.clientNumber} — ${client.displayName}` }}
        reactivationMode={reactivationPayment}
        onClose={() => {
          setPaymentOpen(false);
          setReactivationPayment(false);
        }}
      />
    </>
  );
}


